import React, { useState, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import axios from 'axios'
import html2canvas from 'html2canvas'

import LowerSection from '../components/Dashboard/Sections/LowerSection'
import MiddleSection from '../components/Dashboard/Sections/MiddleSection'
import UpperSection from '../components/Dashboard/Sections/UpperSection'
import { D3StackedBarGraph } from '../components/D3/StackedBarChart'
import Tour from "../components/OnboardingTour/Tour";
import { useSelector } from "react-redux";
import { Box, Button } from "@mui/material"
import { useNavigate } from "react-router-dom";

import NewsPopUp, { shouldOpenNewsPopup } from '../components/NewsPopUp'
import PasswordAgeBanner from '../components/PasswordAgeBanner'

const menuName = 'dashboard'

const expandElement = async (selector) => {
    const element = document.querySelector(selector);
    if (element) {
        const isExpanded = element.getAttribute('aria-expanded') === 'true';
        if (!isExpanded) {
            element.click();
            await new Promise(resolve => setTimeout(resolve, 200));
        }
    }
};

const steps = [
    {
        target: 'body',
        content: <Trans i18nKey="dashboard.tour.content1" />,
        placement: 'center',
        customAction: async ({ type }) => {
            if (type === 'step:before') {
                await expandElement('#sidebarMeasure');
                await expandElement('#sidebarMeasureEmission');
                await expandElement('#sidebarMeasureEmissionScopeThree');
            }
        },
    },
    {
        target: '#nav-sidebarMeasure',
        content: <Trans i18nKey="dashboard.tour.content2" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#sidebarMeasure',
        content: <Trans i18nKey="dashboard.tour.content3" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#sidebarMeasureEmission',
        content: <Trans i18nKey="dashboard.tour.content4" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#sidebarMeasureEmissionScopeOneTwo',
        content: <Trans i18nKey="dashboard.tour.content5" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#sidebarMeasureEmissionScopeThree',
        content: <Trans i18nKey="dashboard.tour.content6" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#sidebarMeasureRevenue',
        content: <Trans i18nKey="dashboard.tour.content10" components={{ b: <b /> }} />,
        placement: 'right',
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#nav-sidebarAnalytic',
        content: <Trans i18nKey="dashboard.tour.content8" components={{ b: <b /> }} />,
        placement: 'right',
        customAction: async ({ type }) => {
            if (type === 'step:before') {
                await expandElement('#sidebarAnalytic');
                window.dispatchEvent(new Event('resize'));
            }
        },
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: '#nav-sidebarReport',
        content: <Trans i18nKey="dashboard.tour.content7" components={{ b: <b /> }} />,
        placement: 'right',
        customAction: async ({ type }) => {
            if (type === 'step:before') {
                await expandElement('#sidebarReport');
                window.dispatchEvent(new Event('resize'));
            }
        },
        styles: { spotlight: { boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' } },
    },
    {
        target: 'body',
        content: <Trans i18nKey="dashboard.tour.content9" />,
        placement: 'center',
    },
];

const onDownloadImage = async (element, label) => {
    const root = document.getElementById(element)
    if (!root) return

    const canvas = await html2canvas(root, {
        ignoreElements: (el) => el?.dataset?.html2canvasIgnore === 'true',
    })

    const image = canvas.toDataURL('image/png', 1.0)

    const a = document.createElement('a')
    a.style.display = 'none'
    a.download = label
    a.href = image
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

function Dashboard() {
    const { t } = useTranslation()
    const account = useSelector((state) => state.account.value)
    const [selectedYear, setSelectedYear] = useState()
    const [yearOptions, setYearOptions] = useState([])
    const [companyOptions, setCompanyOptions] = useState([])
    const [tourRun, setTourRun] = useState(null)
    const navigate = useNavigate();

    useEffect(() => {
        const shouldRun =
            !Array.isArray(account?.tours) || !account.tours.includes(menuName);

        setTourRun(shouldRun);
    }, [account?.tours]);

    useEffect(() => {
        const controller = new AbortController()

        const getYears = async () => {
            const response = await axios({
                withCredentials: true,
                method: 'GET',
                headers: { 'content-type': 'application/json' },
                url: `${process.env.REACT_APP_BASE_SERVER}/record-year-and-company-summaries-lookup`,
                signal: controller.signal,
            })
            if (response.data.ok) {
                if (Array.isArray(response.data.data.years)) {
                    const lastYear = new Date().getFullYear()
                    setYearOptions(response.data.data.years)
                    if (response.data.data.years.length > 0) {
                        const findLastYear = response.data.data.years.find(d => d.year === lastYear)
                        if (findLastYear) {
                            setSelectedYear(findLastYear.year)
                        } else {
                            setSelectedYear(response.data.data.years[0].year)
                        }
                    }
                }

                if (Array.isArray(response.data.data.companies)) {
                    setCompanyOptions(response.data.data.companies)
                }
            }
        }
        getYears()

        return () => controller.abort()
    }, [])

    const onChangeYear = (event) => {
        setSelectedYear(Number(event.target.value))
    }

    // ⬇️ ADD: News popup state
    const VERSION = "1.1.4";
    const [newsOpen, setNewsOpen] = useState(false);
    const [suppressed, setSuppressed] = useState(() => !shouldOpenNewsPopup(VERSION)); // true = seen
    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (tourRun !== false) return;

        const closedThisSession = sessionStorage.getItem("news_popup_closed") === "1";
        if (!hasAutoOpenedRef.current && !closedThisSession && !suppressed) {
            setNewsOpen(true);
            hasAutoOpenedRef.current = true;
        }
    }, [tourRun, suppressed]);

    useEffect(() => {
        if (tourRun === true && newsOpen) {
            setNewsOpen(false);
        }
    }, [tourRun, newsOpen]);

    const handleNewsClose = () => {
        setNewsOpen(false);
        sessionStorage.setItem("news_popup_closed", "1"); // prevent re-open this session
        setSuppressed(!shouldOpenNewsPopup(VERSION));
    };

    const handleSuppressChange = (isSuppressed) => {
        setSuppressed(isSuppressed);
    };

    return (
        <div className='d-flex flex-column gap-3' style={{ padding: '94px 24px 84px' }}>
            <PasswordAgeBanner />
            {/* Onboarding Tour */}
            <Tour
                run={tourRun === true}
                steps={steps}
                menu={menuName}
                allowPrev={false}
                onEnd={(result) => {
                    // Support both shapes: either onEnd() only, or onEnd(result)
                    const status = result?.status;

                    // "finished" means user went through all steps
                    if (status === "finished" || status === "FINISHED") {
                        setTourRun(false);
                        navigate("/emission/create");
                        return;
                    }

                    // if skipped/closed, just stop
                    setTourRun(false);
                }}
            />

            {/* Header */}
            <div className="align-items-center d-flex justify-content-between mb-4">
                <h4 className="mb-sm-0" style={{ color: '#09564B' }}>
                    {t('menu.label1')}
                </h4>

                <div className="d-flex align-items-center gap-2">
                    <Button variant="contained" color="primary"
                        onClick={() => {
                            setNewsOpen(false);
                            setTourRun(true);
                        }}
                        aria-label="Start onboarding tour"
                    >
                        {t('dashboard.startTour')}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setNewsOpen(true)}
                        sx={{ position: 'relative' }}
                    >
                        {t('menu.newsPopUp')}
                        {!suppressed && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    bgcolor: 'error.main',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: 6,
                                    right: 6,
                                }}
                            />
                        )}
                    </Button>


                    {selectedYear && (
                        <select
                            id="selectedEmissionYear"
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={selectedYear}
                            onChange={onChangeYear}
                        >
                            {yearOptions.map((yearOpt) => (
                                <option key={yearOpt.year} value={yearOpt.year}>
                                    {yearOpt.year}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {/* Sections */}
            <UpperSection companyOptions={companyOptions} selectedYear={selectedYear} />

            <MiddleSection
                companyOptions={companyOptions}
                onDownloadImage={onDownloadImage}
                selectedYear={selectedYear}
            />

            <LowerSection
                companyOptions={companyOptions}
                onDownloadImage={onDownloadImage}
                selectedYear={selectedYear}
            />

            <D3StackedBarGraph />

            {/* ⬇️ ADD: News Pop Up — big Dialog (opens on first login per version) */}
            <NewsPopUp
                open={newsOpen}
                onClose={handleNewsClose}
                onSuppressChange={handleSuppressChange}
            />
        </div>
    )
}

export default Dashboard
